import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import type { MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox'
import Map, { GeolocateControl, Layer, Marker, NavigationControl, Source } from 'react-map-gl/mapbox'
import useSlowConnectionDetection from 'components/Pages/Wrapped/hooks/useSlowConnectionDetection'
import { getMapboxAccessToken, MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import { MapboxGlobalStyles } from 'tripPlanner/components/ChatMap/styles'
import MapPitchToggleControl from 'tripPlanner/components/ChatMap/MapPitchToggleControl'
import MapStyleToggleControl from 'tripPlanner/components/ChatMap/MapStyleToggleControl'
import {
  DEFAULT_MAP_STYLE_MODE,
  getMapboxConfig,
  getMapboxStyle,
  type MapPitchMode,
  type MapStyleMode,
} from 'tripPlanner/components/ChatMap/map3DConfig'
import HoverPreview from './PlaceHoverPreview/HoverPreview'
import ExploreOfferHoverPreview from './PlaceHoverPreview/ExploreOfferHoverPreview'
import WishlistOfferHoverPreview from './PlaceHoverPreview/WishlistOfferHoverPreview'
import type { TripItineraryData, MapCameraPosition } from 'tripPlanner/api/types'
import ChatMapMarkerContent from './ChatMapMarkerContent'
import { useMapClusters, ClusterMarker, isClusterFeature } from './ChatMapClustering'
import { useMarkerHover, type HoveredMarkerState } from './useMarkerHover'
import {
  isValidCoordinate,
  MARKER_TYPE_CHAT_HOTEL,
  MARKER_TYPE_CHAT_EXPERIENCE,
  MARKER_TYPE_SUGGESTED_HOTEL,
  MARKER_TYPE_SUGGESTED_EXPERIENCE,
  MARKER_TYPE_EXPLORE_HOTEL,
  MARKER_TYPE_ITEM,
  MARKER_TYPE_EXPLORE_EXPERIENCE,
  MARKER_TYPE_STOP,
  isExperienceOfferMarkerType,
  isOfferMarkerType,
  type MapMarkerComponent,
  type MapCoordinate,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import {
  DEFAULT_VIEW_STATE,
  EMPTY_FIT_BOUNDS_COORDINATES,
  buildMapViewState,
  MAP_STYLE,
  FOG_CONFIG,
  PATH_LINE_PAINT,
  REGION_FILL_PAINT,
  REGION_OUTLINE_PAINT,
  NON_CLUSTER_MARKER_STYLE,
  Wrapper,
  MapContainer,
  MapLoadingSkeleton,
  nextInstanceId,
} from './chatMapConstants'
import type {
  MapViewState,
  MapBounds,
  MarkerSelectionData,
  MapPathComponent,
  MapRegionComponent,
} from './chatMapConstants'
import { useMapGlobeRotation } from './useMapGlobeRotation'
import { useMapCamera } from './useMapCamera'
import { useMapGeolocation } from './useMapGeolocation'
import { useMapResize } from './useMapResize'
import { useMapParentSync } from './useMapParentSync'
import { useMap3DStyle } from 'tripPlanner/components/ChatMap/useMap3DStyle'
import { useMapLightPreset } from 'tripPlanner/components/ChatMap/useMapLightPreset'
import { useIntl } from 'react-intl'

export type { MarkerSelectionData, MapViewState, MapBounds } from './chatMapConstants'

type Props = {
  data: {
    markers?: Array<MapMarkerComponent>
    paths?: Array<MapPathComponent>
    regions?: Array<MapRegionComponent>
  }
  onMarkerSelect?: (data: MarkerSelectionData | undefined) => void
  highlightedMarkerId?: string
  fillContainer?: boolean
  shouldResetToGlobe?: boolean
  onViewStateChange?: (viewState: MapViewState) => void
  onUserMoveStart?: () => void
  onBoundsChange?: (bounds: MapBounds) => void
  isDrawerOpen?: boolean
  chatKey?: string
  deemphasizeBaseMarkers?: boolean
  exploreOffers?: Array<ExploreOffer>
  disableClustering?: boolean
  onMarkerHover?: (markerId: string | undefined) => void
  onOfferClick?: (data: { offerId: string; offerType?: string }) => void
  onOfferPreviewClick?: (marker: Pick<MapMarkerComponent, 'markerId' | 'offerId'>) => void
  itemHoverPreview?: 'place' | 'offer-card'
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  initialCamera?: MapCameraPosition
  aiCamera?: MapCameraPosition
  aiCameraKey?: number
  fitBoundsCoordinates?: Array<MapCoordinate>
  fitBoundsKey?: number
  onFitBoundsHandled?: () => void
  onAiCameraClear?: () => void
  mapPitchMode?: MapPitchMode
  onMapPitchModeChange?: (mode: MapPitchMode) => void
  mapStyleMode?: MapStyleMode
  onMapStyleModeChange?: (mode: MapStyleMode) => void
}

function shouldShowPlaceHoverPreview(
  hoveredMarker: HoveredMarkerState | null | undefined,
  itemHoverPreview: Props['itemHoverPreview'],
): hoveredMarker is HoveredMarkerState & { placeId: string } {
  return Boolean(
    hoveredMarker &&
    hoveredMarker.markerType !== MARKER_TYPE_CHAT_HOTEL &&
    hoveredMarker.markerType !== MARKER_TYPE_SUGGESTED_HOTEL &&
    hoveredMarker.markerType !== MARKER_TYPE_CHAT_EXPERIENCE &&
    hoveredMarker.markerType !== MARKER_TYPE_SUGGESTED_EXPERIENCE &&
    hoveredMarker.markerType !== MARKER_TYPE_EXPLORE_HOTEL &&
    hoveredMarker.markerType !== MARKER_TYPE_EXPLORE_EXPERIENCE &&
    itemHoverPreview !== 'offer-card' &&
    hoveredMarker.placeId,
  )
}

function ChatMapWidget({
  data,
  onMarkerSelect,
  highlightedMarkerId,
  fillContainer = false,
  shouldResetToGlobe,
  onViewStateChange,
  onUserMoveStart,
  onBoundsChange,
  isDrawerOpen,
  chatKey,
  deemphasizeBaseMarkers = false,
  exploreOffers,
  disableClustering = false,
  onMarkerHover,
  onOfferClick,
  onOfferPreviewClick,
  itemHoverPreview = 'place',
  itineraryData,
  onSendMessage,
  initialCamera,
  aiCamera,
  aiCameraKey = 0,
  fitBoundsCoordinates = EMPTY_FIT_BOUNDS_COORDINATES,
  fitBoundsKey = 0,
  onFitBoundsHandled,
  onAiCameraClear,
  mapPitchMode = '2d',
  onMapPitchModeChange,
  mapStyleMode = DEFAULT_MAP_STYLE_MODE,
  onMapStyleModeChange,
}: Props) {
  const intl = useIntl()
  const mapRef = useRef<MapRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [localMapStyleMode, setLocalMapStyleMode] = useState<MapStyleMode>(mapStyleMode)
  const instanceId = useMemo(nextInstanceId, [])
  const currentMapStyleMode = onMapStyleModeChange ? mapStyleMode : localMapStyleMode

  const initialViewState = useMemo(
    () => {
      if (aiCamera) {
        return buildMapViewState(aiCamera, mapPitchMode)
      }
      if (initialCamera) {
        return buildMapViewState(initialCamera, mapPitchMode)
      }
      return DEFAULT_VIEW_STATE
    },
    // Intentional: initialViewState is set once at mount. Subsequent camera
    // changes animate via useMapCamera (flyTo/jumpTo), not by re-initializing.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Cluster zoom — the only React state tracking map position
  const clusterZoomRef = useRef(Math.floor(initialViewState.zoom))
  const [clusterZoom, setClusterZoom] = useState(Math.floor(initialViewState.zoom))

  const { prefersReducedMotion, isSlowConnection, saveData } = useSlowConnectionDetection()
  const shouldSkipAnimation = prefersReducedMotion || isSlowConnection || saveData
  const lightPreset = useMapLightPreset({ enabled: currentMapStyleMode === DEFAULT_MAP_STYLE_MODE })

  useMap3DStyle({ mapRef, isMapLoaded, lightPreset })

  const markers = useMemo(() => (data.markers ?? []).filter(isValidCoordinate), [data.markers])
  const hasActiveCamera = Boolean(aiCamera || initialCamera)
  const shouldSpin = markers.length === 0 && !hasActiveCamera

  const { stopRotation } = useMapGlobeRotation({
    mapRef,
    isMapLoaded,
    shouldSpin,
    shouldSkipAnimation,
    chatKey,
    aiCameraActive: hasActiveCamera,
  })

  const paths = useMemo(() => {
    return (data.paths ?? [])
      .map(path => ({
        ...path,
        points: (path.points ?? []).filter(isValidCoordinate),
      }))
      .filter(path => path.points.length >= 2)
  }, [data.paths])

  const regions = useMemo(() => {
    return (data.regions ?? [])
      .map(region => ({
        ...region,
        polygon: (region.polygon ?? []).filter(isValidCoordinate),
      }))
      .filter(region => region.polygon.length >= 3)
  }, [data.regions])

  const { handleMoveStart } = useMapCamera({
    mapRef,
    isMapLoaded,
    shouldSkipAnimation,
    chatKey,
    markers,
    paths,
    regions,
    shouldResetToGlobe,
    shouldSpin,
    initialCamera,
    aiCamera,
    aiCameraKey,
    fitBoundsCoordinates,
    fitBoundsKey,
    onFitBoundsHandled,
    stopRotation,
    onAiCameraClear,
    mapPitchMode,
  })

  useMapGeolocation({
    mapRef,
    isMapLoaded,
    hasData: Boolean(markers.length > 0 || aiCamera || initialCamera),
    shouldSkipAnimation,
  })

  useMapResize(mapRef, containerRef)

  const { handleMoveEnd } = useMapParentSync(
    mapRef, isMapLoaded, onViewStateChange, onBoundsChange,
  )

  const handleMoveStartWithUserSnapshot = useCallback((evt: Parameters<typeof handleMoveStart>[0]) => {
    handleMoveStart(evt)
    if (evt.originalEvent) {
      onUserMoveStart?.()
    }
  }, [handleMoveStart, onUserMoveStart])

  const {
    hoveredMarker,
    clearHover,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverClose,
  } = useMarkerHover({ onMarkerHover })

  const handleMove = useCallback((evt: ViewStateChangeEvent) => {
    const floored = Math.floor(evt.viewState.zoom)
    if (floored !== clusterZoomRef.current) {
      clusterZoomRef.current = floored
      setClusterZoom(floored)
    }
    clearHover()
  }, [clearHover])

  const handleLoad = useCallback(() => {
    setIsMapLoaded(true)
  }, [])

  const handleMapStyleModeChange = useCallback((mode: MapStyleMode) => {
    if (onMapStyleModeChange) {
      onMapStyleModeChange(mode)
      return
    }
    setLocalMapStyleMode(mode)
  }, [onMapStyleModeChange])

  useEffect(() => {
    if (!onMapStyleModeChange) {
      setLocalMapStyleMode(mapStyleMode)
    }
  }, [mapStyleMode, onMapStyleModeChange])

  useEffect(() => {
    if (isDrawerOpen === false) onMarkerSelect?.(undefined)
  }, [isDrawerOpen, onMarkerSelect])

  const handleMarkerSelect = useCallback((index: number) => {
    const marker = markers[index]
    if (!marker) return
    onMarkerSelect?.({ marker })
  }, [markers, onMarkerSelect])

  const getMarkerKey = useCallback((marker: MapMarkerComponent, index: number): string => {
    if (marker.markerId) return `${instanceId}-marker-${marker.markerId}`
    if (marker.placeId) return `${instanceId}-marker-${marker.placeId}`
    return `${instanceId}-marker-${marker.lat}-${marker.lng}-${marker.label ?? index}`
  }, [instanceId])

  const handleMarkerClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    const { index: markerIndex, markerType, offerId, markerId } = event.currentTarget.dataset
    if (!markerIndex) return
    clearHover()
    // Wishlist markers: navigate to offer page. Only fires when onOfferPreviewClick
    // is provided (wishlists context) — trip chat MARKER_TYPE_ITEM markers fall through
    // to handleMarkerSelect below.
    if (markerType === MARKER_TYPE_ITEM && offerId && onOfferPreviewClick) {
      onOfferPreviewClick({ markerId, offerId })
      return
    }
    if (isOfferMarkerType(markerType)) {
      const isExperience = isExperienceOfferMarkerType(markerType)
      if (offerId) onOfferClick?.({ offerId, offerType: isExperience ? 'experience' : undefined })
      return
    }
    handleMarkerSelect(parseInt(markerIndex, 10))
  }, [clearHover, handleMarkerSelect, onOfferClick, onOfferPreviewClick])

  const handlePopoverClick = useCallback(() => {
    if (!hoveredMarker) return
    clearHover()
    if (hoveredMarker.markerType === MARKER_TYPE_ITEM && itemHoverPreview === 'offer-card') {
      if (hoveredMarker.offerId) {
        onOfferPreviewClick?.({
          markerId: hoveredMarker.markerId,
          offerId: hoveredMarker.offerId,
        })
        if (!onOfferPreviewClick) {
          onOfferClick?.({ offerId: hoveredMarker.offerId })
        }
      }
      return
    }

    if (isOfferMarkerType(hoveredMarker.markerType)) {
      const isExperience = isExperienceOfferMarkerType(hoveredMarker.markerType)
      if (hoveredMarker.offerId) onOfferClick?.({ offerId: hoveredMarker.offerId, offerType: isExperience ? 'experience' : undefined })
      return
    }
    handleMarkerSelect(hoveredMarker.index)
  }, [hoveredMarker, clearHover, handleMarkerSelect, itemHoverPreview, onOfferClick, onOfferPreviewClick])

  const hoveredExploreOffer = useMemo(() => {
    if ((hoveredMarker?.markerType !== MARKER_TYPE_EXPLORE_HOTEL && hoveredMarker?.markerType !== MARKER_TYPE_EXPLORE_EXPERIENCE) || !hoveredMarker?.offerId) return undefined
    return exploreOffers?.find(o => o.id === hoveredMarker.offerId)
  }, [hoveredMarker, exploreOffers])

  const isOverlayMarker = useCallback((m: MapMarkerComponent) => {
    return isOfferMarkerType(m.markerType)
  }, [])

  const itineraryMarkers = useMemo(
    () => markers.filter(m => m.source === 'itinerary' && !isOverlayMarker(m)),
    [markers, isOverlayMarker],
  )
  const nonItineraryMarkers = useMemo(
    () => markers
      .map((m, i) => ({ marker: m, originalIndex: i }))
      .filter(({ marker }) => marker.source !== 'itinerary' || isOverlayMarker(marker)),
    [markers, isOverlayMarker],
  )

  const itineraryToFullIndexMap = useMemo(() => {
    const indexMap: Record<number, number> = {}
    let itinIdx = 0
    markers.forEach((m, index) => {
      if (m.source === 'itinerary' && !isOverlayMarker(m)) {
        indexMap[itinIdx] = index
        itinIdx++
      }
    })
    return indexMap
  }, [markers, isOverlayMarker])

  const { clusters, handleClusterClick } = useMapClusters({
    markers: itineraryMarkers,
    mapRef,
    zoom: clusterZoom,
    disableClustering,
  })

  const pathFeatureCollection = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: paths.map(path => ({
      type: 'Feature' as const,
      properties: { label: path.label ?? '' },
      geometry: {
        type: 'LineString' as const,
        coordinates: path.points.map(point => [point.lng, point.lat]),
      },
    })),
  }), [paths])

  const regionFeatureCollection = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: regions.map(region => ({
      type: 'Feature' as const,
      properties: { label: region.label ?? '' },
      geometry: {
        type: 'Polygon' as const,
        coordinates: [region.polygon.map(point => [point.lng, point.lat])],
      },
    })),
  }), [regions])

  const markerNodes = useMemo(() => {
    const clusteredNodes = clusters.map((feature) => {
      const [lng, lat] = feature.geometry.coordinates

      if (isClusterFeature(feature)) {
        const { cluster_id: clusterId, point_count: pointCount } = feature.properties
        return (
          <ClusterMarker
            key={`cluster-${clusterId}`}
            clusterId={clusterId}
            lng={lng}
            lat={lat}
            pointCount={pointCount}
            onClick={handleClusterClick}
          />
        )
      }

      const marker = feature.properties
      const fullIndex = itineraryToFullIndexMap[marker.originalIndex] ?? marker.originalIndex
      const anchor = marker.markerType === MARKER_TYPE_STOP ? 'bottom' : 'center'
      return (
        <Marker
          key={getMarkerKey(marker, fullIndex)}
          longitude={marker.lng}
          latitude={marker.lat}
          anchor={anchor}
        >
          <div
            data-place-id={marker.placeId}
            data-place-name={marker.label}
            data-marker-id={marker.markerId}
            data-marker-type={marker.markerType}
            data-offer-id={marker.offerId}
            data-title={marker.title}
            data-description={marker.description}
            data-image-id={marker.imageId}
            data-image-ids={marker.imageIds ? JSON.stringify(marker.imageIds) : undefined}
            data-index={fullIndex}
            onClick={handleMarkerClick}
            onMouseEnter={handleMarkerMouseEnter}
            onMouseLeave={handleMarkerMouseLeave}
          >
            <ChatMapMarkerContent
              marker={marker}
              deemphasizeBaseMarkers={deemphasizeBaseMarkers}
              isHighlighted={highlightedMarkerId != null && marker.markerId === highlightedMarkerId}
            />
          </div>
        </Marker>
      )
    })

    const nonClusteredNodes = nonItineraryMarkers.map(({ marker, originalIndex }) => {
      const anchor = marker.markerType === MARKER_TYPE_STOP ? 'bottom' : 'center'
      return (
        <Marker
          key={getMarkerKey(marker, originalIndex)}
          longitude={marker.lng}
          latitude={marker.lat}
          anchor={anchor}
          style={NON_CLUSTER_MARKER_STYLE}
        >
          <div
            data-place-id={marker.placeId}
            data-place-name={marker.label}
            data-marker-id={marker.markerId}
            data-marker-type={marker.markerType}
            data-offer-id={marker.offerId}
            data-title={marker.title}
            data-description={marker.description}
            data-image-id={marker.imageId}
            data-image-ids={marker.imageIds ? JSON.stringify(marker.imageIds) : undefined}
            data-index={originalIndex}
            onClick={handleMarkerClick}
            onMouseEnter={handleMarkerMouseEnter}
            onMouseLeave={handleMarkerMouseLeave}
          >
            <ChatMapMarkerContent
              marker={marker}
              deemphasizeBaseMarkers={deemphasizeBaseMarkers}
              isHighlighted={highlightedMarkerId != null && marker.markerId === highlightedMarkerId}
            />
          </div>
        </Marker>
      )
    })

    return [...clusteredNodes, ...nonClusteredNodes]
  }, [clusters, itineraryToFullIndexMap, nonItineraryMarkers, getMarkerKey, handleMarkerClick, handleMarkerMouseEnter, handleMarkerMouseLeave, deemphasizeBaseMarkers, highlightedMarkerId, handleClusterClick])

  const mapboxAccessToken = getMapboxAccessToken(MAPBOX_ACCESS_TOKEN_USE_CASES.tripPlanner)

  if (!mapboxAccessToken) {
    return (
      <Wrapper className={clsx({ 'fill-container': fillContainer })}>
        <MapContainer className={clsx({ 'fill-container': fillContainer })}>
          <span>{intl.formatMessage({
            id: 'N1wlozfCxNO0uKUi1ATr',
            defaultMessage: 'Map unavailable - missing configuration',
            description: 'Fallback message for missing mapbox configuration in the trip planner AI chat',
          })}
          </span>
        </MapContainer>
      </Wrapper>
    )
  }

  return (
    <Wrapper className={clsx({ 'fill-container': fillContainer })}>
      <MapContainer ref={containerRef} className={clsx({ 'fill-container': fillContainer })}>
        <MapboxGlobalStyles />
        {!isMapLoaded && <MapLoadingSkeleton />}
        <Map
          ref={mapRef}
          mapboxAccessToken={mapboxAccessToken}
          initialViewState={initialViewState}
          onMove={handleMove}
          onMoveStart={handleMoveStartWithUserSnapshot}
          onMoveEnd={handleMoveEnd}
          onLoad={handleLoad}
          style={MAP_STYLE}
          mapStyle={getMapboxStyle(currentMapStyleMode)}
          config={getMapboxConfig(lightPreset)}
          fog={FOG_CONFIG}
          scrollZoom
          attributionControl={false}
          projection="globe"
        >
          {markerNodes}
          {pathFeatureCollection.features.length > 0 && (
            <Source
              id={`${instanceId}-paths`}
              type="geojson"
              data={pathFeatureCollection}
            >
              <Layer
                id={`${instanceId}-paths-line`}
                type="line"
                paint={PATH_LINE_PAINT}
              />
            </Source>
          )}
          {regionFeatureCollection.features.length > 0 && (
            <Source
              id={`${instanceId}-regions`}
              type="geojson"
              data={regionFeatureCollection}
            >
              <Layer
                id={`${instanceId}-regions-fill`}
                type="fill"
                paint={REGION_FILL_PAINT}
              />
              <Layer
                id={`${instanceId}-regions-outline`}
                type="line"
                paint={REGION_OUTLINE_PAINT}
              />
            </Source>
          )}
          <NavigationControl position="bottom-right" showCompass visualizePitch />
          <MapPitchToggleControl position="bottom-right" onPitchModeChange={onMapPitchModeChange} />
          <MapStyleToggleControl
            position="bottom-right"
            mapStyleMode={currentMapStyleMode}
            onMapStyleModeChange={handleMapStyleModeChange}
          />
          <GeolocateControl position="bottom-right" />
        </Map>

        {(hoveredMarker?.markerType === MARKER_TYPE_CHAT_HOTEL || hoveredMarker?.markerType === MARKER_TYPE_SUGGESTED_HOTEL) && hoveredMarker.offerId && (
          <HoverPreview
            type="hotel"
            offerId={hoveredMarker.offerId}
            offerName={hoveredMarker.placeName}
            linkRect={hoveredMarker.rect}
            onClick={handlePopoverClick}
            onClose={handlePopoverClose}
            onMouseEnter={handlePopoverMouseEnter}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
          />
        )}
        {/* type="hotel" is intentional — HoverPreview uses useOffer which handles both hotels and experiences, then differentiates via offer?.type at render time */}
        {(hoveredMarker?.markerType === MARKER_TYPE_CHAT_EXPERIENCE || hoveredMarker?.markerType === MARKER_TYPE_SUGGESTED_EXPERIENCE) && hoveredMarker.offerId && (
          <HoverPreview
            type="hotel"
            offerId={hoveredMarker.offerId}
            offerName={hoveredMarker.placeName}
            linkRect={hoveredMarker.rect}
            onClick={handlePopoverClick}
            onClose={handlePopoverClose}
            onMouseEnter={handlePopoverMouseEnter}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
          />
        )}
        {hoveredExploreOffer && (hoveredMarker?.markerType === MARKER_TYPE_EXPLORE_HOTEL || hoveredMarker?.markerType === MARKER_TYPE_EXPLORE_EXPERIENCE) && (
          <ExploreOfferHoverPreview
            offer={hoveredExploreOffer}
            linkRect={hoveredMarker.rect}
            onClick={handlePopoverClick}
            onClose={handlePopoverClose}
            onMouseEnter={handlePopoverMouseEnter}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
          />
        )}
        {hoveredMarker?.markerType === MARKER_TYPE_ITEM && itemHoverPreview === 'offer-card' && (
          <WishlistOfferHoverPreview
            title={hoveredMarker.title ?? hoveredMarker.placeName}
            description={hoveredMarker.description}
            imageIds={hoveredMarker.imageIds ?? []}
            imageId={hoveredMarker.imageId}
            linkRect={hoveredMarker.rect}
            onClick={handlePopoverClick}
            onClose={handlePopoverClose}
            onMouseEnter={handlePopoverMouseEnter}
          />
        )}
        {shouldShowPlaceHoverPreview(hoveredMarker, itemHoverPreview) && (
          <HoverPreview
            type="place"
            placeId={hoveredMarker.placeId}
            placeName={hoveredMarker.placeName}
            linkRect={hoveredMarker.rect}
            onClick={handlePopoverClick}
            onClose={handlePopoverClose}
            onMouseEnter={handlePopoverMouseEnter}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            hideAddToTrip={hoveredMarker.markerType === MARKER_TYPE_STOP}
          />
        )}
      </MapContainer>
    </Wrapper>
  )
}

export default ChatMapWidget
